import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  useLocation,
  useNavigate,
} from "react-router-dom";

import Header from "../../master/SideBar";
import Nav from "../../master/NavBar";
import MasterV2 from "../MasterV2";

import {
  LoadingDiv,
  SweetAlertShow,
} from "../../master/Master";

import { courseList } from "../../../api/Courses";
import { batchList } from "../../../api/Batch";
import { StaffList } from "../../../api/Staff";

import {
  studentAttendanceListMonthlyV4,
} from "../../../api/Attendance";

import {
  formatToDate,
  formatToTime,
} from "../../master/FormatToDateTime";

/* =========================================================
   HELPERS
========================================================= */

const BATCH_NOT_SELECTED = "";
const ALL_BATCHES = "__all__";

const getCurrentMonth = () => {
  const date = new Date();

  return `${date.getFullYear()}-${String(
    date.getMonth() + 1
  ).padStart(2, "0")}`;
};

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const formatMonthTitle = (value) => {
  if (!value) return "";

  const [year, month] = value.split("-");

  return new Date(
    Number(year),
    Number(month) - 1,
    1
  ).toLocaleDateString("en-IN", {
    month: "long",
    year: "numeric",
  });
};

const safeFormatDate = (value) => {
  if (!value) return null;

  try {
    return formatToDate(value);
  } catch {
    return value;
  }
};

const safeFormatTime = (value) => {
  if (!value) return "";

  try {
    return formatToTime(value);
  } catch {
    return value;
  }
};

const normalizeDate = (value) => {
  if (!value) return null;

  const formatted = safeFormatDate(value);

  if (!formatted) return null;

  const date = new Date(formatted);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  date.setHours(0, 0, 0, 0);

  return date;
};

const getInitials = (name) =>
  String(name || "?")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");

const downloadBlob = (
  content,
  fileName,
  type
) => {
  const blob = new Blob(
    ["\ufeff", content],
    { type }
  );

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");

  anchor.href = url;
  anchor.download = fileName;

  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();

  URL.revokeObjectURL(url);
};

/* =========================================================
   COMPONENT
========================================================= */

export default function MonthlyAttendanceRegister() {
  const navigate = useNavigate();
  const location = useLocation();

  /* =======================================================
     READ URL FILTERS
  ======================================================= */

  const initialParams = useMemo(
    () => new URLSearchParams(location.search),
    []
  );

  const urlBatchId =
    initialParams.get("batch_id") || "";

  const urlAllBatches =
    initialParams.get("all_batches") === "1";

  const initialBatchSelection =
    urlAllBatches
      ? ALL_BATCHES
      : urlBatchId || BATCH_NOT_SELECTED;

  /* =======================================================
     FILTER STATE
  ======================================================= */

  const [filters, setFilters] = useState({
    session_id:
      initialParams.get("session_id") || "",

    course_id:
      initialParams.get("course_id") || "",

    batch_id: initialBatchSelection,

    staff_id:
      initialParams.get("staff_id") || "",

    student_type:
      initialParams.get("student_type") || "",

    month:
      initialParams.get("month") ||
      getCurrentMonth(),
  });

  /* =======================================================
     OTHER STATE
  ======================================================= */

  const [loading, setLoading] = useState(false);

  const [
    filterLoading,
    setFilterLoading,
  ] = useState(false);

  const [data, setData] = useState({});

  const [courses, setCourses] = useState([]);
  const [batches, setBatches] = useState([]);
  const [staffs, setStaffs] = useState([]);
  const [sessions, setSessions] = useState([]);

  const [search, setSearch] = useState("");

  const [
    attendanceView,
    setAttendanceView,
  ] = useState("all");

  const [
    printMenuOpen,
    setPrintMenuOpen,
  ] = useState(false);

  /* =======================================================
     IMPORTANT:
     REPORT IS READY ONLY AFTER USER SELECTS:
     - a specific batch
     OR
     - All Batches
  ======================================================= */

  const hasBatchSelection =
    filters.batch_id !== BATCH_NOT_SELECTED;

  const isAllBatches =
    filters.batch_id === ALL_BATCHES;

  /* =======================================================
     MONTH
  ======================================================= */

  const [yearString, monthString] =
    (filters.month || getCurrentMonth()).split("-");

  const year = Number(yearString);
  const month = Number(monthString);

  const totalDays = new Date(
    year,
    month,
    0
  ).getDate();

  const days = useMemo(
    () =>
      Array.from(
        { length: totalDays },
        (_, index) => index + 1
      ),
    [totalDays]
  );

  const today = useMemo(() => {
    const date = new Date();

    date.setHours(0, 0, 0, 0);

    return date;
  }, []);

  /* =======================================================
     SESSIONS
  ======================================================= */

  useEffect(() => {
    try {
      const stored = JSON.parse(
        localStorage.getItem("sessions") ?? "[]"
      );

      setSessions(
        Array.isArray(stored) ? stored : []
      );
    } catch {
      setSessions([]);
    }
  }, []);

  useEffect(() => {
    if (!sessions.length) return;

    if (filters.session_id) return;

    const active = sessions.find(
      (item) => Number(item?.status) === 1
    );

    if (!active?.id) return;

    setFilters((previous) => ({
      ...previous,
      session_id: String(active.id),

      /*
       * Do NOT auto select batch.
       */
      batch_id: BATCH_NOT_SELECTED,
    }));
  }, [
    sessions,
    filters.session_id,
  ]);

  /* =======================================================
     COURSES
  ======================================================= */

  const getCourses = async () => {
    try {
      setFilterLoading(true);

      const response = await courseList();

      if (response?.data?.status) {
        setCourses(
          Array.isArray(response?.data?.data)
            ? response.data.data
            : []
        );
      } else {
        setCourses([]);
      }
    } catch (error) {
      console.error(
        "Course loading error:",
        error
      );

      setCourses([]);
    } finally {
      setFilterLoading(false);
    }
  };

  /* =======================================================
     STAFF
  ======================================================= */

  const getStaffs = async () => {
    try {
      const response = await StaffList(
        "status=1"
      );

      if (response?.data?.status) {
        setStaffs(
          Array.isArray(response?.data?.data)
            ? response.data.data
            : []
        );
      } else {
        setStaffs([]);
      }
    } catch (error) {
      console.error(
        "Staff loading error:",
        error
      );

      setStaffs([]);
    }
  };

  useEffect(() => {
    getCourses();
    getStaffs();
  }, []);

  /* =======================================================
     BATCH LIST

     This still loads the dropdown options.
     It DOES NOT load attendance data.
  ======================================================= */

  const getBatches = async () => {
    if (!filters.session_id) {
      setBatches([]);
      return;
    }

    try {
      setFilterLoading(true);

      let addUrl = "&status=1";

      addUrl +=
        `&session_id=${encodeURIComponent(
          filters.session_id
        )}`;

      if (filters.course_id) {
        addUrl +=
          `&course_id=${encodeURIComponent(
            filters.course_id
          )}`;
      }

      const response = await batchList(addUrl);

      if (response?.data?.status) {
        setBatches(
          Array.isArray(response?.data?.data)
            ? response.data.data
            : []
        );
      } else {
        setBatches([]);
      }
    } catch (error) {
      console.error(
        "Batch loading error:",
        error
      );

      setBatches([]);
    } finally {
      setFilterLoading(false);
    }
  };

  useEffect(() => {
    getBatches();

    // eslint-disable-next-line
  }, [
    filters.session_id,
    filters.course_id,
  ]);

  /* =======================================================
     QUERY

     All Batches:
     DO NOT send batch_id.

     Specific Batch:
     send batch_id.
  ======================================================= */

  const buildQuery = () => {
    const queryFilters = {
      session_id: filters.session_id,
      course_id: filters.course_id,
      staff_id: filters.staff_id,
      student_type: filters.student_type,
      month: filters.month,
    };

    if (
      filters.batch_id &&
      filters.batch_id !== ALL_BATCHES
    ) {
      queryFilters.batch_id =
        filters.batch_id;
    }

    let addUrl = "";

    Object.entries(queryFilters).forEach(
      ([key, value]) => {
        if (
          value !== undefined &&
          value !== null &&
          String(value) !== ""
        ) {
          addUrl +=
            `&${key}=${encodeURIComponent(
              value
            )}`;
        }
      }
    );

    return addUrl;
  };

  /* =======================================================
     ATTENDANCE API
  ======================================================= */

  const getAttendanceList = async () => {
    /*
     * IMPORTANT:
     * Do NOT fetch until batch has been selected.
     */
    if (!hasBatchSelection) {
      setData({});
      return;
    }

    if (
      !filters.session_id ||
      !filters.month
    ) {
      setData({});
      return;
    }

    try {
      setLoading(true);

      const addUrl = buildQuery();

      console.log(
        "Monthly Attendance Query:",
        addUrl
      );

      const response =
        await studentAttendanceListMonthlyV4(
          addUrl
        );

      if (response?.data?.status) {
        setData(
          response?.data?.data || {}
        );
      } else {
        setData({});

        if (response?.data?.message) {
          SweetAlertShow(
            "info",
            "Information",
            response.data.message
          );
        }
      }
    } catch (error) {
      console.error(
        "Monthly attendance error:",
        error
      );

      setData({});

      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ||
        error?.message ||
        "Unable to load monthly attendance."
      );
    } finally {
      setLoading(false);
    }
  };

  /* =======================================================
     AUTO FETCH

     Notice:
     no API call when batch_id === ""
  ======================================================= */

  useEffect(() => {
    if (!hasBatchSelection) {
      setData({});
      return;
    }

    if (
      !filters.session_id ||
      !filters.month
    ) {
      return;
    }

    getAttendanceList();

    // eslint-disable-next-line
  }, [
    filters.session_id,
    filters.course_id,
    filters.batch_id,
    filters.staff_id,
    filters.student_type,
    filters.month,
  ]);

  /* =======================================================
     URL SYNC
  ======================================================= */

  useEffect(() => {
    if (!filters.session_id) return;

    const params = new URLSearchParams();

    params.set(
      "session_id",
      filters.session_id
    );

    if (filters.month) {
      params.set(
        "month",
        filters.month
      );
    }

    if (filters.course_id) {
      params.set(
        "course_id",
        filters.course_id
      );
    }

    /*
     * Specific batch
     */
    if (
      filters.batch_id &&
      filters.batch_id !== ALL_BATCHES
    ) {
      params.set(
        "batch_id",
        filters.batch_id
      );
    }

    /*
     * All batches
     */
    if (
      filters.batch_id === ALL_BATCHES
    ) {
      params.set(
        "all_batches",
        "1"
      );
    }

    if (filters.staff_id) {
      params.set(
        "staff_id",
        filters.staff_id
      );
    }

    if (filters.student_type) {
      params.set(
        "student_type",
        filters.student_type
      );
    }

    navigate(
      {
        pathname: location.pathname,
        search: `?${params.toString()}`,
      },
      {
        replace: true,
      }
    );

    // eslint-disable-next-line
  }, [
    filters.session_id,
    filters.course_id,
    filters.batch_id,
    filters.staff_id,
    filters.student_type,
    filters.month,
  ]);

  /* =======================================================
     API ROWS
  ======================================================= */

  const rows = Array.isArray(data?.data)
    ? data.data
    : Array.isArray(data)
      ? data
      : [];

  /* =======================================================
     SELECTED ITEMS
  ======================================================= */

  const selectedSession = sessions.find(
    (item) =>
      String(item?.id) ===
      String(filters.session_id)
  );

  const selectedCourse = courses.find(
    (item) =>
      String(item?.id) ===
      String(filters.course_id)
  );

  const selectedBatch = batches.find(
    (item) =>
      String(item?.id) ===
      String(filters.batch_id)
  );

  const selectedStaff = staffs.find(
    (item) =>
      String(item?.id) ===
      String(filters.staff_id)
  );

  /* =======================================================
     MONTH DAY INFORMATION
  ======================================================= */

  const monthDayInfo = useMemo(() => {
    const result = {};

    days.forEach((day) => {
      const dateString =
        `${filters.month}-${String(day).padStart(
          2,
          "0"
        )}`;

      const dateObject =
        normalizeDate(dateString);

      const isPastOrToday =
        dateObject
          ? dateObject <= today
          : false;

      const formattedDate =
        safeFormatDate(dateString);

      /*
       * Preserve old report behavior:
       * If nobody has attendance on a past day,
       * consider that day a holiday.
       */
      const anyAttendance = rows.some(
        (student) =>
          Array.isArray(
            student?.attendances
          ) &&
          student.attendances.some(
            (attendance) =>
              safeFormatDate(
                attendance?.date
              ) === formattedDate
          )
      );

      result[day] = {
        dateString,
        formattedDate,
        dateObject,
        isPastOrToday,

        isHoliday:
          isPastOrToday &&
          !anyAttendance,
      };
    });

    return result;
  }, [
    days,
    filters.month,
    rows,
    today,
  ]);

  /* =======================================================
     ANALYSE STUDENT
  ======================================================= */

  const analyseStudent = (student) => {
    const attendanceMap = new Map();

    (
      Array.isArray(student?.attendances)
        ? student.attendances
        : []
    ).forEach((attendance) => {
      const key = safeFormatDate(
        attendance?.date
      );

      if (key) {
        attendanceMap.set(
          key,
          attendance
        );
      }
    });

    const leaveMap = new Map();

    (
      Array.isArray(
        student?.applied_leave_request
      )
        ? student.applied_leave_request
        : []
    ).forEach((leave) => {
      const key = safeFormatDate(
        leave?.date
      );

      if (key) {
        leaveMap.set(
          key,
          leave
        );
      }
    });

    const admissionValue =
      student?.created_at ||
      student?.latest_course_and_fee
        ?.created_at;

    const admissionDate =
      normalizeDate(admissionValue);

    let present = 0;
    let absent = 0;
    let notMarked = 0;
    let workingDays = 0;
    let holidays = 0;
    let leaveDays = 0;

    const dayStatuses = {};

    days.forEach((day) => {
      const info =
        monthDayInfo[day];

      const attendance =
        attendanceMap.get(
          info?.formattedDate
        );

      const leave =
        leaveMap.get(
          info?.formattedDate
        );

      const hasLeave = Boolean(leave);

      if (hasLeave) {
        leaveDays += 1;
      }

      const isAdmitted =
        !admissionDate ||
        !info?.dateObject ||
        info.dateObject >= admissionDate;

      let status = "future";

      if (!isAdmitted) {
        status = "na";
      } else if (
        Number(attendance?.check_in) === 1
      ) {
        status = "present";
        present += 1;

        if (
          info?.isPastOrToday &&
          !info?.isHoliday
        ) {
          workingDays += 1;
        }
      } else if (
        Number(attendance?.check_in) === 2
      ) {
        status = "absent";
        absent += 1;

        if (
          info?.isPastOrToday &&
          !info?.isHoliday
        ) {
          workingDays += 1;
        }
      } else if (info?.isHoliday) {
        status = "holiday";
        holidays += 1;
      } else if (info?.isPastOrToday) {
        status = "not_marked";
        notMarked += 1;
        workingDays += 1;
      }

      dayStatuses[day] = {
        status,
        attendance,
        leave,
        hasLeave,
        isAdmitted,
        ...info,
      };
    });

    const percentage =
      workingDays > 0
        ? Number(
          (
            (present / workingDays) *
            100
          ).toFixed(1)
        )
        : 0;

    return {
      student,
      present,
      absent,
      notMarked,
      workingDays,
      holidays,
      leaveDays,
      percentage,
      dayStatuses,
    };
  };

  const analysedRows = useMemo(
    () => rows.map(analyseStudent),

    // eslint-disable-next-line
    [
      rows,
      monthDayInfo,
      days,
    ]
  );

  /* =======================================================
     SEARCH + VIEW
  ======================================================= */

  const filteredRows = useMemo(() => {
    const keyword = search
      .trim()
      .toLowerCase();

    return analysedRows.filter(
      (row) => {
        const student = row.student;

        const course =
          student?.latest_course_and_fee
            ?.course?.title || "";

        const batch =
          student?.latest_course_and_fee
            ?.batch?.title || "";

        const searchable = [
          student?.name,
          student?.registration_no,
          student?.phone,
          student?.mobile,
          course,
          batch,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        if (
          keyword &&
          !searchable.includes(keyword)
        ) {
          return false;
        }

        if (
          attendanceView === "low"
        ) {
          return (
            row.workingDays > 0 &&
            row.percentage < 75
          );
        }

        if (
          attendanceView === "absent"
        ) {
          return row.absent > 0;
        }

        if (
          attendanceView === "not_marked"
        ) {
          return row.notMarked > 0;
        }

        if (
          attendanceView === "perfect"
        ) {
          return (
            row.workingDays > 0 &&
            row.percentage >= 100
          );
        }

        return true;
      }
    );
  }, [
    analysedRows,
    search,
    attendanceView,
  ]);

  /* =======================================================
     SUMMARY
  ======================================================= */

  const summary = useMemo(() => {
    let present = 0;
    let absent = 0;
    let notMarked = 0;
    let workingDays = 0;
    let lowAttendance = 0;

    analysedRows.forEach((row) => {
      present += row.present;
      absent += row.absent;
      notMarked += row.notMarked;
      workingDays += row.workingDays;

      if (
        row.workingDays > 0 &&
        row.percentage < 75
      ) {
        lowAttendance += 1;
      }
    });

    const percentage =
      workingDays > 0
        ? Number(
          (
            (present / workingDays) *
            100
          ).toFixed(1)
        )
        : 0;

    return {
      students: analysedRows.length,
      present,
      absent,
      notMarked,
      workingDays,
      lowAttendance,
      percentage,
    };
  }, [analysedRows]);

  /* =======================================================
     FILTER HANDLERS
  ======================================================= */

  const handleSessionChange = (event) => {
    setSearch("");
    setAttendanceView("all");
    setData({});

    setFilters((previous) => ({
      ...previous,

      session_id: event.target.value,

      course_id: "",

      /*
       * IMPORTANT:
       * Reset to Select Batch
       */
      batch_id: BATCH_NOT_SELECTED,
    }));
  };

  const handleCourseChange = (event) => {
    setSearch("");
    setAttendanceView("all");
    setData({});

    setFilters((previous) => ({
      ...previous,

      course_id: event.target.value,

      /*
       * IMPORTANT:
       * course change means user needs to
       * select batch again.
       */
      batch_id: BATCH_NOT_SELECTED,
    }));
  };

  const handleBatchChange = (event) => {
    const value = event.target.value;

    setSearch("");
    setAttendanceView("all");
    setData({});

    setFilters((previous) => ({
      ...previous,
      batch_id: value,
    }));
  };

  const resetFilters = () => {
    const active = sessions.find(
      (item) => Number(item?.status) === 1
    );

    setSearch("");
    setAttendanceView("all");
    setData({});

    setFilters({
      session_id:
        active?.id
          ? String(active.id)
          : "",

      course_id: "",

      /*
       * Default = Select Batch
       */
      batch_id: BATCH_NOT_SELECTED,

      staff_id: "",
      student_type: "",
      month: getCurrentMonth(),
    });
  };

  /* =======================================================
     DAY CELL
  ======================================================= */

  const renderDayCell = (info) => {
    if (!info) return null;

    if (info.status === "present") {
      const inTime =
        info.attendance?.check_in_time;

      const outTime =
        info.attendance?.check_out_time;

      if (inTime || outTime) {
        return (
          <div className="monthly-time-cell">

            {inTime && (
              <span className="monthly-in-time">
                {safeFormatTime(inTime)}
              </span>
            )}

            {outTime && (
              <span className="monthly-out-time">
                {safeFormatTime(outTime)}
              </span>
            )}

            {info.hasLeave && (
              <small>L</small>
            )}

          </div>
        );
      }

      return (
        <span className="monthly-code present">
          P
          {info.hasLeave && (
            <small>/L</small>
          )}
        </span>
      );
    }

    if (info.status === "absent") {
      return (
        <span className="monthly-code absent">
          A
          {info.hasLeave && (
            <small>/L</small>
          )}
        </span>
      );
    }

    if (info.status === "holiday") {
      return (
        <span className="monthly-code holiday">
          H
          {info.hasLeave && (
            <small>/L</small>
          )}
        </span>
      );
    }

    if (info.status === "na") {
      return (
        <span className="monthly-code na">
          NA
          {info.hasLeave && (
            <small>/L</small>
          )}
        </span>
      );
    }

    if (
      info.status === "not_marked"
    ) {
      return (
        <span className="monthly-code not-marked">
          N
          {info.hasLeave && (
            <small>/L</small>
          )}
        </span>
      );
    }

    if (info.hasLeave) {
      return (
        <span className="monthly-code leave">
          L
        </span>
      );
    }

    return (
      <span className="monthly-future">
        -
      </span>
    );
  };

  /* =======================================================
     PRINT META
  ======================================================= */

  const getPrintMetaHtml = () => `
    <div class="print-meta">

      <div>
        <span>Month</span>
        <strong>
          ${escapeHtml(
    formatMonthTitle(filters.month)
  )}
        </strong>
      </div>

      <div>
        <span>Session</span>
        <strong>
          ${escapeHtml(
    selectedSession?.session || "-"
  )}
        </strong>
      </div>

      <div>
        <span>Course</span>
        <strong>
          ${escapeHtml(
    selectedCourse?.title ||
    "All Courses"
  )}
        </strong>
      </div>

      <div>
        <span>Batch</span>
        <strong>
          ${escapeHtml(
    isAllBatches
      ? "All Batches"
      : selectedBatch?.title || "-"
  )}
        </strong>
      </div>

      <div>
        <span>Class Teacher</span>
        <strong>
          ${escapeHtml(
    selectedStaff?.name ||
    "All Teachers"
  )}
        </strong>
      </div>

    </div>
  `;

  const openPrintWindow = (
    title,
    body
  ) => {
    const printWindow = window.open(
      "",
      "_blank",
      "width=1500,height=900"
    );

    if (!printWindow) {
      SweetAlertShow(
        "info",
        "Popup Blocked",
        "Please allow popups for this website and try again."
      );

      return;
    }

    printWindow.document.open();

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>

      <head>

        <meta charset="UTF-8" />

        <title>
          ${escapeHtml(title)}
        </title>

        <style>

          @page {
            size: A3 landscape;
            margin: 7mm;
          }

          * {
            box-sizing: border-box;
          }

          body {
            margin: 0;

            font-family:
              Arial,
              Helvetica,
              sans-serif;

            color: #222;

            font-size: 9px;

            -webkit-print-color-adjust:
              exact;

            print-color-adjust:
              exact;
          }

          .print-header {
            display: flex;

            align-items: flex-end;

            justify-content:
              space-between;

            gap: 20px;

            padding-bottom: 8px;

            margin-bottom: 8px;

            border-bottom:
              2px solid #333;
          }

          .print-header h1 {
            margin: 0;
            font-size: 18px;
          }

          .print-header p {
            margin: 3px 0 0;

            color: #666;

            font-size: 9px;
          }

          .print-legend {
            text-align: right;

            line-height: 1.5;
          }

          .print-meta {
            display: grid;

            grid-template-columns:
              repeat(5, 1fr);

            gap: 5px;

            margin-bottom: 8px;
          }

          .print-meta > div {
            padding: 5px;

            border:
              1px solid #ccc;
          }

          .print-meta span {
            display: block;

            margin-bottom: 2px;

            color: #777;

            font-size: 7px;

            text-transform:
              uppercase;
          }

          .print-meta strong {
            font-size: 8px;
          }

          .print-summary {
            display: grid;

            grid-template-columns:
              repeat(5, 1fr);

            gap: 5px;

            margin-bottom: 8px;
          }

          .print-summary div {
            padding: 5px;

            border:
              1px solid #ccc;

            text-align: center;
          }

          .print-summary span {
            display: block;

            color: #666;

            font-size: 7px;
          }

          .print-summary strong {
            display: block;

            margin-top: 2px;

            font-size: 13px;
          }

          table {
            width: 100%;

            border-collapse:
              collapse;

            table-layout:
              fixed;
          }

          thead {
            display:
              table-header-group;
          }

          tr {
            page-break-inside:
              avoid;
          }

          th,
          td {
            padding: 2px;

            border:
              1px solid #999;

            font-size: 7px;

            text-align: center;

            vertical-align:
              middle;
          }

          th {
            background: #eee;

            font-weight: 700;
          }

          .serial {
            width: 22px;
          }

          .student {
            width: 115px;

            text-align: left;
          }

          .reg {
            width: 70px;
          }

          .day {
            width: 25px;
          }

          .overview {
            width: 80px;
          }

          .percentage {
            width: 42px;
          }

          .student strong {
            display: block;

            font-size: 7px;
          }

          .student small {
            display: block;

            margin-top: 1px;

            color: #666;

            font-size: 6px;
          }

          .present {
            color: #087b42;
            font-weight: bold;
          }

          .absent {
            color: #c32626;
            font-weight: bold;
          }

          .not-marked {
            color: #b76d00;
            font-weight: bold;
          }

          .holiday,
          .na {
            color: #666;
            font-weight: bold;
          }

          .low {
            background: #fff0f0;
          }

          .print-footer {
            display: flex;

            justify-content:
              space-between;

            margin-top: 12px;

            padding-top: 8px;

            border-top:
              1px solid #aaa;

            color: #666;

            font-size: 7px;
          }

        </style>

      </head>

      <body>

        ${body}

        <script>

          window.onload =
            function () {

              setTimeout(
                function () {

                  window.focus();
                  window.print();

                },
                350
              );

            };

          window.onafterprint =
            function () {
              window.close();
            };

        </script>

      </body>

      </html>
    `);

    printWindow.document.close();
  };

  /* =======================================================
     PRINT REGISTER
  ======================================================= */

  const printRegister = () => {
    setPrintMenuOpen(false);

    if (!hasBatchSelection) {
      SweetAlertShow(
        "info",
        "Select Batch",
        "Please select a batch or All Batches first."
      );

      return;
    }

    if (!filteredRows.length) {
      SweetAlertShow(
        "info",
        "No Data",
        "There are no students available to print."
      );

      return;
    }

    const dayHeaders = days
      .map(
        (day) =>
          `<th class="day">${day}</th>`
      )
      .join("");

    const bodyRows = filteredRows
      .map((row, index) => {
        const student = row.student;

        const dayCells = days
          .map((day) => {
            const info =
              row.dayStatuses[day];

            let text = "";

            if (
              info?.status === "present"
            ) {
              text =
                info?.attendance
                  ?.check_in_time
                  ? safeFormatTime(
                    info.attendance
                      .check_in_time
                  )
                  : "P";
            } else if (
              info?.status === "absent"
            ) {
              text = "A";
            } else if (
              info?.status === "holiday"
            ) {
              text = "H";
            } else if (
              info?.status === "na"
            ) {
              text = "NA";
            } else if (
              info?.status ===
              "not_marked"
            ) {
              text = "N";
            } else if (
              info?.hasLeave
            ) {
              text = "L";
            }

            if (
              info?.hasLeave &&
              text &&
              text !== "L"
            ) {
              text += "/L";
            }

            const css =
              info?.status === "present"
                ? "present"
                : info?.status === "absent"
                  ? "absent"
                  : info?.status ===
                    "not_marked"
                    ? "not-marked"
                    : info?.status ===
                      "holiday"
                      ? "holiday"
                      : info?.status ===
                        "na"
                        ? "na"
                        : "";

            return `
              <td class="${css}">
                ${escapeHtml(text)}
              </td>
            `;
          })
          .join("");

        return `
          <tr
            class="${row.workingDays > 0 &&
            row.percentage < 75
            ? "low"
            : ""
          }"
          >

            <td>
              ${index + 1}
            </td>

            <td class="student">

              <strong>
                ${escapeHtml(
            student?.name || "-"
          )}
              </strong>

              <small>
                ${escapeHtml(
            student?.gender || ""
          )}
              </small>

            </td>

            <td class="reg">
              ${escapeHtml(
            student?.registration_no ||
            "-"
          )}
            </td>

            ${dayCells}

            <td class="overview">

              P:${row.present}
              &nbsp;

              A:${row.absent}
              &nbsp;

              N:${row.notMarked}

              <br />

              WD:${row.workingDays}

            </td>

            <td
              class="percentage ${row.percentage < 75
            ? "absent"
            : "present"
          }"
            >
              ${row.percentage}%
            </td>

          </tr>
        `;
      })
      .join("");

    const body = `

      <div class="print-header">

        <div>

          <h1>
            Monthly Student Attendance Register
          </h1>

          <p>
            ${escapeHtml(
      formatMonthTitle(
        filters.month
      )
    )}
          </p>

        </div>

        <div class="print-legend">

          <strong>P</strong>
          Present &nbsp; | &nbsp;

          <strong>A</strong>
          Absent &nbsp; | &nbsp;

          <strong>N</strong>
          Not Marked &nbsp; | &nbsp;

          <strong>H</strong>
          Holiday &nbsp; | &nbsp;

          <strong>NA</strong>
          Not Admitted &nbsp; | &nbsp;

          <strong>L</strong>
          Leave

        </div>

      </div>

      ${getPrintMetaHtml()}

      <div class="print-summary">

        <div>
          <span>Students</span>
          <strong>
            ${summary.students}
          </strong>
        </div>

        <div>
          <span>Present Marks</span>
          <strong>
            ${summary.present}
          </strong>
        </div>

        <div>
          <span>Absent Marks</span>
          <strong>
            ${summary.absent}
          </strong>
        </div>

        <div>
          <span>Not Marked</span>
          <strong>
            ${summary.notMarked}
          </strong>
        </div>

        <div>
          <span>Attendance</span>
          <strong>
            ${summary.percentage}%
          </strong>
        </div>

      </div>

      <table>

        <thead>

          <tr>

            <th class="serial">
              #
            </th>

            <th class="student">
              Student
            </th>

            <th class="reg">
              Reg. No.
            </th>

            ${dayHeaders}

            <th class="overview">
              Overview
            </th>

            <th class="percentage">
              %
            </th>

          </tr>

        </thead>

        <tbody>
          ${bodyRows}
        </tbody>

      </table>

      <div class="print-footer">

        <span>
          Generated:
          ${escapeHtml(
      new Date().toLocaleString(
        "en-IN"
      )
    )}
        </span>

        <span>
          Class Teacher Signature
          ____________________

          &nbsp;&nbsp;&nbsp;

          Principal Signature
          ____________________
        </span>

      </div>
    `;

    openPrintWindow(
      `${formatMonthTitle(
        filters.month
      )} Attendance Register`,
      body
    );
  };

  /* =======================================================
     PRINT SUMMARY
  ======================================================= */

  const printSummary = () => {
    setPrintMenuOpen(false);

    if (!hasBatchSelection) {
      SweetAlertShow(
        "info",
        "Select Batch",
        "Please select a batch or All Batches first."
      );

      return;
    }

    if (!filteredRows.length) {
      SweetAlertShow(
        "info",
        "No Data",
        "There are no students available to print."
      );

      return;
    }

    const studentRows = filteredRows
      .map(
        (row, index) => `
          <tr>

            <td>
              ${index + 1}
            </td>

            <td class="student">

              <strong>
                ${escapeHtml(
          row.student?.name || "-"
        )}
              </strong>

              <small>
                ${escapeHtml(
          row.student
            ?.registration_no || ""
        )}
              </small>

            </td>

            <td>
              ${row.workingDays}
            </td>

            <td class="present">
              ${row.present}
            </td>

            <td class="absent">
              ${row.absent}
            </td>

            <td class="not-marked">
              ${row.notMarked}
            </td>

            <td>
              ${row.leaveDays}
            </td>

            <td
              class="${row.percentage < 75
            ? "absent"
            : "present"
          }"
            >
              ${row.percentage}%
            </td>

          </tr>
        `
      )
      .join("");

    const body = `

      <div class="print-header">

        <div>

          <h1>
            Monthly Attendance Summary
          </h1>

          <p>
            ${escapeHtml(
      formatMonthTitle(
        filters.month
      )
    )}
          </p>

        </div>

      </div>

      ${getPrintMetaHtml()}

      <div class="print-summary">

        <div>
          <span>Total Students</span>
          <strong>
            ${summary.students}
          </strong>
        </div>

        <div>
          <span>Present</span>
          <strong>
            ${summary.present}
          </strong>
        </div>

        <div>
          <span>Absent</span>
          <strong>
            ${summary.absent}
          </strong>
        </div>

        <div>
          <span>Not Marked</span>
          <strong>
            ${summary.notMarked}
          </strong>
        </div>

        <div>
          <span>Overall Attendance</span>
          <strong>
            ${summary.percentage}%
          </strong>
        </div>

      </div>

      <table>

        <thead>

          <tr>
            <th>#</th>
            <th>Student</th>
            <th>Working Days</th>
            <th>Present</th>
            <th>Absent</th>
            <th>Not Marked</th>
            <th>Leave</th>
            <th>Attendance %</th>
          </tr>

        </thead>

        <tbody>
          ${studentRows}
        </tbody>

      </table>
    `;

    openPrintWindow(
      "Monthly Attendance Summary",
      body
    );
  };

  /* =======================================================
     EXCEL
  ======================================================= */

  const exportExcel = () => {
    if (!hasBatchSelection) {
      SweetAlertShow(
        "info",
        "Select Batch",
        "Please select a batch or All Batches first."
      );

      return;
    }

    if (!filteredRows.length) {
      SweetAlertShow(
        "info",
        "No Data",
        "There are no students available to export."
      );

      return;
    }

    const headers = [
      "#",
      "Student Name",
      "Gender",
      "Mobile",
      "Registration No",
      "Course",
      "Batch",

      ...days.map(
        (day) =>
          `${String(day).padStart(
            2,
            "0"
          )}/${String(month).padStart(
            2,
            "0"
          )}/${year}`
      ),

      "Working Days",
      "Present",
      "Absent",
      "Not Marked",
      "Leave",
      "Attendance %",
    ];

    const excelRows =
      filteredRows.map(
        (row, index) => {
          const student = row.student;

          const course =
            student
              ?.latest_course_and_fee
              ?.course?.title || "";

          const batch =
            student
              ?.latest_course_and_fee
              ?.batch?.title || "";

          const dayValues =
            days.map((day) => {
              const info =
                row.dayStatuses[day];

              if (
                info?.status ===
                "present"
              ) {
                let value =
                  info?.attendance
                    ?.check_in_time
                    ? `P (${safeFormatTime(
                      info.attendance
                        .check_in_time
                    )}${info?.attendance
                      ?.check_out_time
                      ? ` - ${safeFormatTime(
                        info.attendance
                          .check_out_time
                      )}`
                      : ""
                    })`
                    : "P";

                if (info.hasLeave) {
                  value += "/L";
                }

                return value;
              }

              if (
                info?.status ===
                "absent"
              ) {
                return info.hasLeave
                  ? "A/L"
                  : "A";
              }

              if (
                info?.status ===
                "holiday"
              ) {
                return info.hasLeave
                  ? "H/L"
                  : "H";
              }

              if (
                info?.status === "na"
              ) {
                return info.hasLeave
                  ? "NA/L"
                  : "NA";
              }

              if (
                info?.status ===
                "not_marked"
              ) {
                return info.hasLeave
                  ? "N/L"
                  : "N";
              }

              if (info?.hasLeave) {
                return "L";
              }

              return "";
            });

          return [
            index + 1,
            student?.name || "",
            student?.gender || "",
            student?.phone ||
            student?.mobile ||
            "",
            student?.registration_no ||
            "",
            course,
            batch,
            ...dayValues,
            row.workingDays,
            row.present,
            row.absent,
            row.notMarked,
            row.leaveDays,
            `${row.percentage}%`,
          ];
        }
      );

    const tableHtml = `
      <html
        xmlns:o="urn:schemas-microsoft-com:office:office"
        xmlns:x="urn:schemas-microsoft-com:office:excel"
        xmlns="http://www.w3.org/TR/REC-html40"
      >

      <head>
        <meta charset="UTF-8">
      </head>

      <body>

        <table border="1">

          <tr>

            <th
              colspan="${headers.length}"
              style="
                font-size:18px;
                font-weight:bold;
                text-align:center;
              "
            >
              Monthly Student Attendance Register -
              ${escapeHtml(
      formatMonthTitle(
        filters.month
      )
    )}
            </th>

          </tr>

          <tr>

            <td colspan="${headers.length}">

              Session:
              ${escapeHtml(
      selectedSession?.session ||
      "-"
    )}

              | Course:
              ${escapeHtml(
      selectedCourse?.title ||
      "All Courses"
    )}

              | Batch:
              ${escapeHtml(
      isAllBatches
        ? "All Batches"
        : selectedBatch?.title ||
        "-"
    )}

              | Teacher:
              ${escapeHtml(
      selectedStaff?.name ||
      "All Teachers"
    )}

            </td>

          </tr>

          <tr>

            ${headers
        .map(
          (header) =>
            `<th>${escapeHtml(
              header
            )}</th>`
        )
        .join("")}

          </tr>

          ${excelRows
        .map(
          (row) => `
                <tr>

                  ${row
              .map(
                (cell) =>
                  `<td>${escapeHtml(
                    cell
                  )}</td>`
              )
              .join("")}

                </tr>
              `
        )
        .join("")}

        </table>

      </body>

      </html>
    `;

    downloadBlob(
      tableHtml,
      `monthly-attendance-${filters.month}.xls`,
      "application/vnd.ms-excel;charset=utf-8;"
    );
  };

  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <>
      {loading && <LoadingDiv />}

      <div className="layout-wrapper layout-content-navbar attendance-dashboard monthly-attendance-page">
        <div className="layout-container">

          {/* SIDEBAR */}
          <div className="no-print">
            <Header />
          </div>

          <div className="layout-page">

            {/* TOP NAVBAR */}
            <div className="no-print">
              <Nav />
            </div>

            <div className="content-wrapper monthly-content-wrapper">
              <div className="container-fluid flex-grow-1 container-p-y monthly-container">

                {/* ATTENDANCE MASTER V2 */}
                <div className="no-print monthly-master-v2-wrapper">
                  <MasterV2 />
                </div>

                <main className="monthly-main">

                  {/* =================================================
              HEADER
          ================================================= */}
                  {/* 
          <section className="monthly-header">

            <div className="monthly-header-left">

              <button
                type="button"
                className="monthly-back"
                onClick={() => navigate(-1)}
              >
                <i className="ri-arrow-left-line" />
              </button>

              <div className="monthly-header-icon">
                <i className="ri-calendar-2-line" />
              </div>

              <div>

                <div className="monthly-breadcrumb">
                  Attendance Reports
                  <i className="ri-arrow-right-s-line" />
                  Monthly Register
                </div>

                <h1>
                  Monthly Attendance Register
                </h1>

                <p>
                  View complete day-wise student
                  attendance, working days and
                  monthly attendance percentage.
                </p>

              </div>

            </div>

            <div className="monthly-header-actions">

              <button
                type="button"
                className="monthly-action secondary"
                disabled={!hasBatchSelection}
                onClick={getAttendanceList}
              >
                <i className="ri-refresh-line" />
                Refresh
              </button>

              <button
                type="button"
                className="monthly-action excel"
                disabled={!hasBatchSelection}
                onClick={exportExcel}
              >
                <i className="ri-file-excel-2-line" />
                Export Excel
              </button>

              <div className="monthly-print-wrapper">

                <button
                  type="button"
                  className="monthly-action primary"
                  disabled={!hasBatchSelection}
                  onClick={() =>
                    setPrintMenuOpen(
                      (previous) => !previous
                    )
                  }
                >
                  <i className="ri-printer-line" />
                  Print
                  <i className="ri-arrow-down-s-line" />
                </button>

                {printMenuOpen && (
                  <div className="monthly-print-menu">

                    <button
                      type="button"
                      onClick={printRegister}
                    >

                      <span className="print-icon">
                        <i className="ri-table-line" />
                      </span>

                      <span>
                        <strong>
                          Print Register
                        </strong>

                        <small>
                          Full monthly attendance
                          register
                        </small>
                      </span>

                    </button>

                    <button
                      type="button"
                      onClick={printSummary}
                    >

                      <span className="print-icon summary">
                        <i className="ri-bar-chart-box-line" />
                      </span>

                      <span>
                        <strong>
                          Print Summary
                        </strong>

                        <small>
                          Student-wise monthly
                          totals
                        </small>
                      </span>

                    </button>

                  </div>
                )}

              </div>

            </div>

          </section> */}

                  {/* =================================================
              FILTERS
          ================================================= */}

                  <section className="monthly-filter-card">

                    <div className="monthly-card-title">

                      <div>

                        <h2>
                          <i className="ri-filter-3-line" />
                          Monthly Report Filters
                        </h2>

                        <p>
                          First select a batch or All
                          Batches to load attendance.
                        </p>

                      </div>



                      <div className="monthly-header-actions">

                        <button
                          type="button"
                          className="monthly-reset"
                          onClick={resetFilters}
                        >
                          <i className="ri-restart-line" />
                          Reset
                        </button>

                        <button
                          type="button"
                          className="monthly-action secondary"
                          disabled={!hasBatchSelection}
                          onClick={getAttendanceList}
                        >
                          <i className="ri-refresh-line" />
                          Refresh
                        </button>

                        <button
                          type="button"
                          className="monthly-action excel"
                          disabled={!hasBatchSelection}
                          onClick={exportExcel}
                        >
                          <i className="ri-file-excel-2-line" />
                          Export Excel
                        </button>

                        <div className="monthly-print-wrapper">

                          <button
                            type="button"
                            className="monthly-action primary"
                            disabled={!hasBatchSelection}
                            onClick={() =>
                              setPrintMenuOpen(
                                (previous) => !previous
                              )
                            }
                          >
                            <i className="ri-printer-line" />
                            Print
                            <i className="ri-arrow-down-s-line" />
                          </button>

                          {printMenuOpen && (
                            <div className="monthly-print-menu">

                              <button
                                type="button"
                                onClick={printRegister}
                              >

                                <span className="print-icon">
                                  <i className="ri-table-line" />
                                </span>

                                <span>
                                  <strong>
                                    Print Register
                                  </strong>

                                  <small>
                                    Full monthly attendance
                                    register
                                  </small>
                                </span>

                              </button>

                              <button
                                type="button"
                                onClick={printSummary}
                              >

                                <span className="print-icon summary">
                                  <i className="ri-bar-chart-box-line" />
                                </span>

                                <span>
                                  <strong>
                                    Print Summary
                                  </strong>

                                  <small>
                                    Student-wise monthly
                                    totals
                                  </small>
                                </span>

                              </button>

                            </div>
                          )}

                        </div>

                      </div>

                    </div>

                    <div className="monthly-filter-grid">

                      {/* SESSION */}

                      <div className="monthly-field">

                        <label>
                          Academic Session
                        </label>

                        <div className="monthly-control">

                          <i className="ri-graduation-cap-line" />

                          <select
                            value={
                              filters.session_id
                            }
                            onChange={
                              handleSessionChange
                            }
                          >

                            <option value="">
                              Select Session
                            </option>

                            {sessions.map(
                              (session) => (
                                <option
                                  key={session.id}
                                  value={session.id}
                                >
                                  {session.session}

                                  {Number(
                                    session.status
                                  ) === 1
                                    ? " - Active"
                                    : ""}
                                </option>
                              )
                            )}

                          </select>

                        </div>

                      </div>

                      {/* MONTH */}

                      <div className="monthly-field important">

                        <label>
                          Report Month
                        </label>

                        <div className="monthly-control">

                          <i className="ri-calendar-line" />

                          <input
                            type="month"
                            value={filters.month}
                            onChange={(e) => {

                              setData({});

                              setFilters(
                                (previous) => ({
                                  ...previous,

                                  month:
                                    e.target.value,
                                })
                              );

                            }}
                          />

                        </div>

                      </div>

                      {/* COURSE */}

                      <div className="monthly-field">

                        <label>
                          Course
                        </label>

                        <div className="monthly-control">

                          <i className="ri-book-open-line" />

                          <select
                            value={
                              filters.course_id
                            }
                            onChange={
                              handleCourseChange
                            }
                          >

                            <option value="">
                              All Courses
                            </option>

                            {courses.map(
                              (course) => (
                                <option
                                  key={course.id}
                                  value={course.id}
                                >
                                  {course.title}
                                </option>
                              )
                            )}

                          </select>

                        </div>

                      </div>

                      {/* ==========================================
                  BATCH

                  Default:
                  Select Batch

                  Second:
                  All Batches
              ========================================== */}

                      <div className="monthly-field batch-field">

                        <label>
                          Batch
                          <span className="required-star">
                            *
                          </span>
                        </label>

                        <div className="monthly-control">

                          <i className="ri-group-2-line" />

                          <select
                            value={
                              filters.batch_id
                            }
                            onChange={
                              handleBatchChange
                            }
                          >

                            <option
                              value=""
                              disabled
                            >
                              Select Batch
                            </option>

                            <option
                              value={ALL_BATCHES}
                            >
                              All Batches
                            </option>

                            {batches.map(
                              (batch) => (
                                <option
                                  key={batch.id}
                                  value={batch.id}
                                >
                                  {batch.title}

                                  {batch?.course
                                    ?.title
                                    ? ` - ${batch.course.title}`
                                    : ""}
                                </option>
                              )
                            )}

                          </select>

                        </div>

                        {!hasBatchSelection && (
                          <small className="batch-help">
                            Select batch to load report
                          </small>
                        )}

                      </div>

                      {/* TEACHER */}

                      <div className="monthly-field">

                        <label>
                          Class Teacher
                        </label>

                        <div className="monthly-control">

                          <i className="ri-user-star-line" />

                          <select
                            value={
                              filters.staff_id
                            }
                            onChange={(e) => {

                              setData({});

                              setFilters(
                                (previous) => ({
                                  ...previous,

                                  staff_id:
                                    e.target.value,
                                })
                              );

                            }}
                          >

                            <option value="">
                              All Teachers
                            </option>

                            {staffs.map(
                              (staff) => (
                                <option
                                  key={staff.id}
                                  value={staff.id}
                                >
                                  {staff.name}

                                  {staff.registration_no
                                    ? ` (${staff.registration_no})`
                                    : ""}
                                </option>
                              )
                            )}

                          </select>

                        </div>

                      </div>

                      {/* STUDENT TYPE */}

                      <div className="monthly-field">

                        <label>
                          Student Type
                        </label>

                        <div className="monthly-control">

                          <i className="ri-user-settings-line" />

                          <select
                            value={
                              filters.student_type
                            }
                            onChange={(e) => {

                              setData({});

                              setFilters(
                                (previous) => ({
                                  ...previous,

                                  student_type:
                                    e.target.value,
                                })
                              );

                            }}
                          >

                            <option value="">
                              All Students
                            </option>

                            <option value="hostel">
                              Hostel Students
                            </option>
                            <option value="without_hostel">
                              Without Hostel Students
                            </option>

                          </select>

                        </div>

                      </div>

                    </div>

                    {filterLoading && (
                      <div className="monthly-filter-loading">
                        <i className="ri-loader-4-line monthly-spin" />
                        Loading filter options...
                      </div>
                    )}

                  </section>

                  {/* =================================================
              NO BATCH SELECTED

              THIS IS THE DEFAULT TABLE AREA
          ================================================= */}

                  {!hasBatchSelection && (

                    <section className="batch-selection-empty">

                      <div className="batch-selection-icon">
                        <i className="ri-group-2-line" />
                      </div>

                      <h2>
                        Select Batch to View Monthly
                        Attendance
                      </h2>

                      <p>
                        Select a specific batch from the
                        Batch filter above, or select
                        <strong> All Batches </strong>
                        to view the complete monthly
                        attendance register.
                      </p>

                      <div className="batch-selection-info">

                        <span>
                          <i className="ri-calendar-line" />

                          {formatMonthTitle(
                            filters.month
                          )}
                        </span>

                        {selectedSession && (
                          <span>
                            <i className="ri-graduation-cap-line" />

                            {selectedSession.session}
                          </span>
                        )}

                        {selectedCourse && (
                          <span>
                            <i className="ri-book-open-line" />

                            {selectedCourse.title}
                          </span>
                        )}

                      </div>

                      <div className="batch-selection-arrow">
                        <i className="ri-arrow-up-line" />
                        Choose Batch from the filters
                        above
                      </div>

                    </section>

                  )}

                  {/* =================================================
              REPORT
          ================================================= */}

                  {hasBatchSelection && (
                    <>

                      {/* REPORT BAR */}

                      <section className="monthly-report-bar">

                        <div className="monthly-report-month">

                          <span className="month-icon">
                            <i className="ri-calendar-event-line" />
                          </span>

                          <div>

                            <small>
                              Attendance Register
                            </small>

                            <strong>
                              {formatMonthTitle(
                                filters.month
                              )}
                            </strong>

                          </div>

                        </div>

                        <div className="monthly-report-tags">

                          <span>
                            <i className="ri-graduation-cap-line" />

                            {selectedSession
                              ?.session ||
                              "Session"}
                          </span>

                          <span>
                            <i className="ri-book-open-line" />

                            {selectedCourse
                              ?.title ||
                              "All Courses"}
                          </span>

                          <span className="batch-report-tag">
                            <i className="ri-group-line" />

                            {isAllBatches
                              ? "All Batches"
                              : selectedBatch
                                ?.title ||
                              "Batch"}
                          </span>

                          {selectedStaff && (
                            <span>
                              <i className="ri-user-star-line" />
                              {selectedStaff.name}
                            </span>
                          )}

                        </div>

                      </section>

                      {/* SUMMARY */}

                      <section className="monthly-summary-grid">

                        <button
                          type="button"
                          className={
                            `monthly-summary-card students ${attendanceView === "all"
                              ? "selected"
                              : ""
                            }`
                          }
                          onClick={() =>
                            setAttendanceView("all")
                          }
                        >

                          <div className="summary-top">

                            <span>
                              <i className="ri-group-line" />
                            </span>

                            <small>
                              Students
                            </small>

                          </div>

                          <strong>
                            {summary.students}
                          </strong>

                          <p>
                            Total students in report
                          </p>

                        </button>

                        <div className="monthly-summary-card present">

                          <div className="summary-top">

                            <span>
                              <i className="ri-checkbox-circle-line" />
                            </span>

                            <small>
                              Present
                            </small>

                          </div>

                          <strong>
                            {summary.present}
                          </strong>

                          <p>
                            Present attendance marks
                          </p>

                        </div>

                        <button
                          type="button"
                          className={
                            `monthly-summary-card absent ${attendanceView ===
                              "absent"
                              ? "selected"
                              : ""
                            }`
                          }
                          onClick={() =>
                            setAttendanceView(
                              attendanceView ===
                                "absent"
                                ? "all"
                                : "absent"
                            )
                          }
                        >

                          <div className="summary-top">

                            <span>
                              <i className="ri-close-circle-line" />
                            </span>

                            <small>
                              Absences
                            </small>

                          </div>

                          <strong>
                            {summary.absent}
                          </strong>

                          <p>
                            Total absent marks
                          </p>

                        </button>

                        <button
                          type="button"
                          className={
                            `monthly-summary-card pending ${attendanceView ===
                              "not_marked"
                              ? "selected"
                              : ""
                            }`
                          }
                          onClick={() =>
                            setAttendanceView(
                              attendanceView ===
                                "not_marked"
                                ? "all"
                                : "not_marked"
                            )
                          }
                        >

                          <div className="summary-top">

                            <span>
                              <i className="ri-time-line" />
                            </span>

                            <small>
                              Not Marked
                            </small>

                          </div>

                          <strong>
                            {summary.notMarked}
                          </strong>

                          <p>
                            Attendance requiring review
                          </p>

                        </button>

                        <button
                          type="button"
                          className={
                            `monthly-summary-card low ${attendanceView === "low"
                              ? "selected"
                              : ""
                            }`
                          }
                          onClick={() =>
                            setAttendanceView(
                              attendanceView === "low"
                                ? "all"
                                : "low"
                            )
                          }
                        >

                          <div className="summary-top">

                            <span>
                              <i className="ri-error-warning-line" />
                            </span>

                            <small>
                              Below 75%
                            </small>

                          </div>

                          <strong>
                            {summary.lowAttendance}
                          </strong>

                          <p>
                            Low attendance students
                          </p>

                        </button>

                        <div className="monthly-summary-card average">

                          <div className="summary-top">

                            <span>
                              <i className="ri-pie-chart-line" />
                            </span>

                            <small>
                              Attendance
                            </small>

                          </div>

                          <strong>
                            {summary.percentage}%
                          </strong>

                          <p>
                            Overall monthly attendance
                          </p>

                        </div>

                      </section>

                      {/* LEGEND */}

                      <section className="monthly-legend">

                        <strong>
                          Register Legend
                        </strong>

                        <span className="present">
                          P
                          <small>Present</small>
                        </span>

                        <span className="absent">
                          A
                          <small>Absent</small>
                        </span>

                        <span className="not-marked">
                          N
                          <small>Not Marked</small>
                        </span>

                        <span className="holiday">
                          H
                          <small>Holiday</small>
                        </span>

                        <span className="na">
                          NA
                          <small>
                            Not Admitted
                          </small>
                        </span>

                        <span className="leave">
                          L
                          <small>Leave</small>
                        </span>

                      </section>

                      {/* REGISTER */}

                      <section className="monthly-register-card">

                        <div className="monthly-table-heading">

                          <div>

                            <h2>
                              Student Attendance Register
                            </h2>

                            <p>
                              Showing{" "}
                              <strong>
                                {filteredRows.length}
                              </strong>{" "}
                              student
                              {filteredRows.length === 1
                                ? ""
                                : "s"}

                              {" • "}

                              {totalDays} calendar days

                              {" • "}

                              <strong>
                                {isAllBatches
                                  ? "All Batches"
                                  : selectedBatch
                                    ?.title ||
                                  ""}
                              </strong>
                            </p>

                          </div>

                          <div className="monthly-table-tools">

                            <div className="monthly-view-select">

                              <i className="ri-filter-line" />

                              <select
                                value={
                                  attendanceView
                                }
                                onChange={(e) =>
                                  setAttendanceView(
                                    e.target.value
                                  )
                                }
                              >

                                <option value="all">
                                  All Students
                                </option>

                                <option value="low">
                                  Below 75%
                                </option>

                                <option value="absent">
                                  Has Absence
                                </option>

                                <option value="not_marked">
                                  Has Not Marked
                                </option>

                                <option value="perfect">
                                  100% Attendance
                                </option>

                              </select>

                            </div>

                            <div className="monthly-search">

                              <i className="ri-search-line" />

                              <input
                                type="text"
                                value={search}
                                placeholder="Search student, reg. no., batch..."
                                onChange={(e) =>
                                  setSearch(
                                    e.target.value
                                  )
                                }
                              />

                              {search && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    setSearch("")
                                  }
                                >
                                  <i className="ri-close-line" />
                                </button>
                              )}

                            </div>

                            <button
                              type="button"
                              className="monthly-small-excel"
                              onClick={exportExcel}
                            >
                              <i className="ri-file-excel-2-line" />
                              Excel
                            </button>

                          </div>

                        </div>

                        <div className="monthly-table-scroll">

                          <table className="monthly-table">

                            <thead>

                              <tr>

                                <th className="sticky-col serial-col">
                                  #
                                </th>

                                <th className="sticky-col student-col">
                                  Student
                                </th>

                                <th className="sticky-col reg-col">
                                  Reg. No.
                                </th>

                                {days.map((day) => {
                                  const info =
                                    monthDayInfo[day];

                                  return (
                                    <th
                                      key={day}
                                      className={
                                        `day-col ${info?.isHoliday
                                          ? "holiday-day"
                                          : ""
                                        }`
                                      }
                                    >

                                      <strong>
                                        {day}
                                      </strong>

                                      <small>
                                        {info?.dateObject
                                          ?.toLocaleDateString(
                                            "en-IN",
                                            {
                                              weekday:
                                                "short",
                                            }
                                          )
                                          ?.slice(0, 2)}
                                      </small>

                                    </th>
                                  );
                                })}

                                <th className="overview-col">
                                  Overview
                                </th>

                                <th className="percentage-col">
                                  Attendance
                                </th>

                              </tr>

                            </thead>

                            <tbody>

                              {!loading &&
                                filteredRows.length ===
                                0 && (

                                  <tr>

                                    <td
                                      colSpan={
                                        totalDays + 5
                                      }
                                      className="monthly-empty"
                                    >

                                      <i className="ri-calendar-close-line" />

                                      <strong>
                                        No Attendance Data Found
                                      </strong>

                                      <span>
                                        No student attendance
                                        data was found for the
                                        selected filters.
                                      </span>

                                    </td>

                                  </tr>
                                )}

                              {filteredRows.map(
                                (
                                  row,
                                  index
                                ) => {
                                  const student =
                                    row.student;

                                  const course =
                                    student
                                      ?.latest_course_and_fee
                                      ?.course
                                      ?.title || "";

                                  const batch =
                                    student
                                      ?.latest_course_and_fee
                                      ?.batch
                                      ?.title || "";

                                  const lowAttendance =
                                    row.workingDays >
                                    0 &&
                                    row.percentage <
                                    75;

                                  return (
                                    <tr
                                      key={
                                        student?.id ||
                                        index
                                      }
                                      className={
                                        lowAttendance
                                          ? "low-attendance-row"
                                          : ""
                                      }
                                    >

                                      <td className="sticky-col serial-col">
                                        {index + 1}
                                      </td>

                                      <td className="sticky-col student-col">

                                        <div className="monthly-student">

                                          <div className="monthly-avatar">
                                            {getInitials(
                                              student?.name
                                            )}
                                          </div>

                                          <div>

                                            <strong>
                                              {student?.name ||
                                                "-"}
                                            </strong>

                                            <span>
                                              {batch ||
                                                course ||
                                                student?.gender ||
                                                ""}
                                            </span>

                                          </div>

                                        </div>

                                      </td>

                                      <td className="sticky-col reg-col">

                                        <strong className="monthly-reg">
                                          {student?.registration_no ||
                                            "-"}
                                        </strong>

                                        {(student?.phone ||
                                          student?.mobile) && (

                                            <small>
                                              {student?.phone ||
                                                student?.mobile}
                                            </small>

                                          )}

                                      </td>

                                      {days.map(
                                        (day) => (
                                          <td
                                            key={day}
                                            className={
                                              `monthly-day-cell ${row
                                                .dayStatuses[
                                                day
                                              ]
                                                ?.isHoliday
                                                ? "holiday-cell"
                                                : ""
                                              }`
                                            }
                                          >
                                            {renderDayCell(
                                              row
                                                .dayStatuses[
                                              day
                                              ]
                                            )}
                                          </td>
                                        )
                                      )}

                                      <td className="overview-col">

                                        <div className="monthly-overview">

                                          <span className="p">
                                            P
                                            <b>
                                              {row.present}
                                            </b>
                                          </span>

                                          <span className="a">
                                            A
                                            <b>
                                              {row.absent}
                                            </b>
                                          </span>

                                          <span className="n">
                                            N
                                            <b>
                                              {row.notMarked}
                                            </b>
                                          </span>

                                        </div>

                                        <small className="working-days">
                                          {row.workingDays} working days
                                        </small>

                                      </td>

                                      <td className="percentage-col">

                                        <div
                                          className={
                                            `monthly-percentage ${row.percentage >=
                                              90
                                              ? "excellent"
                                              : row.percentage >=
                                                75
                                                ? "good"
                                                : "low"
                                            }`
                                          }
                                        >
                                          {row.percentage}%
                                        </div>

                                        {lowAttendance && (
                                          <small className="low-label">
                                            Low
                                          </small>
                                        )}

                                      </td>

                                    </tr>
                                  );
                                }
                              )}

                            </tbody>

                          </table>

                        </div>

                        <div className="monthly-table-footer">

                          <span>
                            <i className="ri-information-line" />

                            Scroll horizontally to view
                            all dates.
                          </span>

                          <strong>
                            {formatMonthTitle(
                              filters.month
                            )}
                          </strong>

                        </div>

                      </section>

                    </>
                  )}

                </main>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ===================================================
          CSS
      =================================================== */}

      <style>{`

        * {
          box-sizing: border-box;
        }

        .monthly-attendance-page {
          min-height: 100vh;
          background: #f6f7fb;
          color: #474b55;
        }

        .monthly-attendance-page .layout-container {
          min-height: 100vh;
        }

        .monthly-attendance-page .layout-page {
          min-width: 0;
          min-height: 100vh;
        }

        .monthly-content-wrapper {
          width: 100%;
          min-width: 0;
          background: #f6f7fb;
        }

        .monthly-container {
          width: 100%;
          max-width: 100%;
          min-width: 0;
          padding: 16px 18px 40px;
        }

        .monthly-master-v2-wrapper {
          width: 100%;
          min-width: 0;
          margin-bottom: 18px;
        }

        .monthly-main {
          width: 100%;
          max-width: 1800px;
          min-width: 0;
          margin: 0 auto;
          padding: 0;
        }

        /* HEADER */

        .monthly-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 20px;

          padding: 21px;
          margin-bottom: 17px;

          background: #fff;

          border: 1px solid #e2e5ec;
          border-radius: 15px;

          box-shadow:
            0 4px 18px
            rgba(40,45,70,.045);
        }

        .monthly-header-left {
          display: flex;
          align-items: center;
          gap: 13px;
        }

        .monthly-back {
          width: 42px;
          height: 42px;

          display: flex;
          align-items: center;
          justify-content: center;

          flex: 0 0 42px;

          border: 1px solid #dfe2e9;
          border-radius: 10px;

          background: #fff;
          color: #606571;

          font-size: 20px;

          cursor: pointer;
        }

        .monthly-header-icon {
          width: 51px;
          height: 51px;

          display: flex;
          align-items: center;
          justify-content: center;

          flex: 0 0 51px;

          border-radius: 12px;

          background: #eef0ff;
          color: #696cff;

          font-size: 25px;
        }

        .monthly-breadcrumb {
          display: flex;
          align-items: center;
          gap: 3px;

          margin-bottom: 4px;

          color: #969aa5;

          font-size: 12px;
          font-weight: 600;
        }

        .monthly-header h1 {
          margin: 0;

          color: #383c46;

          font-size: 25px;
          font-weight: 700;

          line-height: 1.2;
        }

        .monthly-header p {
          margin: 6px 0 0;

          color: #8d929e;

          font-size: 13px;
          line-height: 1.5;
        }

        .monthly-header-actions {
          display: flex;
          align-items: center;
          gap: 8px;

          flex-shrink: 0;
        }

        .monthly-action {
          min-height: 42px;

          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 7px;

          padding: 9px 14px;

          border-radius: 9px;

          font-size: 13px;
          font-weight: 700;

          cursor: pointer;
          white-space: nowrap;
        }

        .monthly-action:disabled {
          opacity: .48;
          cursor: not-allowed;
        }

        .monthly-action.secondary {
          border: 1px solid #dfe2e9;
          background: #fff;
          color: #5f646e;
        }

        .monthly-action.primary {
          border: 1px solid #696cff;
          background: #696cff;
          color: #fff;
        }

        .monthly-action.excel {
          border: 1px solid #d6e9de;
          background: #effaf3;
          color: #278653;
        }

        /* PRINT */

        .monthly-print-wrapper {
          position: relative;
        }

        .monthly-print-menu {
          position: absolute;

          top: calc(100% + 7px);
          right: 0;

          z-index: 1000;

          width: 290px;

          padding: 7px;

          border: 1px solid #e0e3e9;
          border-radius: 11px;

          background: #fff;

          box-shadow:
            0 15px 40px
            rgba(30,35,55,.15);
        }

        .monthly-print-menu button {
          width: 100%;

          display: flex;
          align-items: center;
          gap: 10px;

          padding: 10px;

          border: 0;
          border-radius: 8px;

          background: transparent;
          color: #4d525d;

          text-align: left;

          cursor: pointer;
        }

        .monthly-print-menu button:hover {
          background: #f7f8fb;
        }

        .print-icon {
          width: 38px;
          height: 38px;

          display: flex;
          align-items: center;
          justify-content: center;

          flex: 0 0 38px;

          border-radius: 8px;

          background: #eef0ff;
          color: #696cff;

          font-size: 18px;
        }

        .print-icon.summary {
          background: #eef8f2;
          color: #278653;
        }

        .monthly-print-menu
        button > span:last-child {
          display: flex;
          flex-direction: column;
        }

        .monthly-print-menu strong {
          font-size: 13px;
        }

        .monthly-print-menu small {
          margin-top: 2px;

          color: #969ba6;

          font-size: 11px;
        }

        /* FILTER */

        .monthly-filter-card {
          padding: 19px;
          margin-bottom: 14px;

          background: #fff;

          border: 1px solid #e2e5ec;
          border-radius: 14px;
        }

        .monthly-card-title {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 15px;

          margin-bottom: 16px;
        }

        .monthly-card-title h2 {
          display: flex;
          align-items: center;
          gap: 7px;

          margin: 0;

          color: #414650;

          font-size: 16px;
          font-weight: 700;
        }

        .monthly-card-title h2 i {
          color: #696cff;
        }

        .monthly-card-title p {
          margin: 4px 0 0;

          color: #969ba6;

          font-size: 12px;
        }

        .monthly-reset {
          display: inline-flex;
          align-items: center;
          gap: 5px;

          padding: 8px 11px;

          border: 1px solid #dfe2e9;
          border-radius: 8px;

          background: #fff;
          color: #646974;

          font-size: 12px;
          font-weight: 700;

          cursor: pointer;
        }

        .monthly-filter-grid {
          display: grid;

          grid-template-columns:
            repeat(
              6,
              minmax(0, 1fr)
            );

          gap: 12px;
        }

        .monthly-field label {
          display: block;

          margin-bottom: 6px;

          color: #575c67;

          font-size: 12px;
          font-weight: 700;
        }

        .required-star {
          margin-left: 3px;
          color: #d74647;
        }

        .monthly-control {
          position: relative;
        }

        .monthly-control > i {
          position: absolute;

          top: 50%;
          left: 11px;

          z-index: 2;

          transform:
            translateY(-50%);

          color: #9499a4;

          font-size: 16px;

          pointer-events: none;
        }

        .monthly-control select,
        .monthly-control input {
          width: 100%;
          height: 44px;

          padding:
            8px 10px 8px 35px;

          border: 1px solid #dce0e8;
          border-radius: 9px;

          outline: none;

          background: #fff;
          color: #4c515b;

          font-size: 13px;
          font-weight: 500;
        }

        .monthly-control select:focus,
        .monthly-control input:focus {
          border-color: #8e91ff;

          box-shadow:
            0 0 0 3px
            rgba(105,108,255,.09);
        }

        .batch-field
        .monthly-control select {
          border-color: #b9bcff;

          background: #fbfbff;
        }

        .batch-help {
          display: block;

          margin-top: 5px;

          color: #696cff;

          font-size: 10px;
          font-weight: 600;
        }

        .monthly-filter-loading {
          margin-top: 10px;

          color: #777c87;

          font-size: 12px;
        }

        /* ================================================
           SELECT BATCH EMPTY STATE
        ================================================ */

        .batch-selection-empty {
          min-height: 440px;

          display: flex;
          align-items: center;
          justify-content: center;
          flex-direction: column;

          padding: 45px 20px;

          border:
            1px dashed #cfd3e5;

          border-radius: 15px;

          background: #fff;

          text-align: center;
        }

        .batch-selection-icon {
          width: 76px;
          height: 76px;

          display: flex;
          align-items: center;
          justify-content: center;

          margin-bottom: 18px;

          border-radius: 50%;

          background: #eef0ff;
          color: #696cff;

          font-size: 34px;
        }

        .batch-selection-empty h2 {
          margin: 0;

          color: #414650;

          font-size: 21px;
          font-weight: 700;
        }

        .batch-selection-empty > p {
          max-width: 580px;

          margin: 9px auto 0;

          color: #8d929e;

          font-size: 13px;
          line-height: 1.7;
        }

        .batch-selection-empty
        > p strong {
          color: #696cff;
        }

        .batch-selection-info {
          display: flex;
          align-items: center;
          justify-content: center;
          flex-wrap: wrap;

          gap: 7px;

          margin-top: 20px;
        }

        .batch-selection-info span {
          display: inline-flex;
          align-items: center;
          gap: 5px;

          padding: 7px 10px;

          border: 1px solid #e1e4eb;
          border-radius: 8px;

          background: #fafbfc;
          color: #656a75;

          font-size: 11px;
          font-weight: 600;
        }

        .batch-selection-arrow {
          display: inline-flex;
          align-items: center;
          gap: 5px;

          margin-top: 24px;

          color: #696cff;

          font-size: 11px;
          font-weight: 700;
        }

        /* REPORT BAR */

        .monthly-report-bar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 15px;

          padding: 11px 14px;
          margin-bottom: 14px;

          border: 1px solid #dfe2eb;
          border-radius: 11px;

          background: #fff;
        }

        .monthly-report-month {
          display: flex;
          align-items: center;
          gap: 9px;
        }

        .month-icon {
          width: 38px;
          height: 38px;

          display: flex;
          align-items: center;
          justify-content: center;

          border-radius: 8px;

          background: #eef0ff;
          color: #696cff;

          font-size: 18px;
        }

        .monthly-report-month div {
          display: flex;
          flex-direction: column;
        }

        .monthly-report-month small {
          color: #979ba6;
          font-size: 10px;
        }

        .monthly-report-month strong {
          margin-top: 1px;

          color: #454a54;
          font-size: 14px;
        }

        .monthly-report-tags {
          display: flex;
          flex-wrap: wrap;
          justify-content: flex-end;
          gap: 6px;
        }

        .monthly-report-tags span {
          display: inline-flex;
          align-items: center;
          gap: 5px;

          padding: 6px 9px;

          border: 1px solid #e3e5eb;
          border-radius: 7px;

          color: #696e78;

          font-size: 10.5px;
          font-weight: 600;
        }

        .batch-report-tag {
          border-color:
            #d9dafe !important;

          background:
            #f7f7ff !important;

          color:
            #696cff !important;
        }

        /* SUMMARY */

        .monthly-summary-grid {
          display: grid;

          grid-template-columns:
            repeat(6, 1fr);

          gap: 11px;

          margin-bottom: 14px;
        }

        .monthly-summary-card {
          min-height: 128px;

          padding: 14px;

          border: 1px solid #e2e5eb;
          border-radius: 12px;

          background: #fff;
          color: inherit;

          text-align: left;
        }

        button.monthly-summary-card {
          cursor: pointer;
        }

        button.monthly-summary-card:hover {
          border-color: #cfd3dc;

          box-shadow:
            0 5px 15px
            rgba(30,35,55,.05);
        }

        .monthly-summary-card.selected {
          border-color: #696cff;

          box-shadow:
            0 0 0 2px
            rgba(105,108,255,.08);
        }

        .summary-top {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .summary-top > span {
          width: 35px;
          height: 35px;

          display: flex;
          align-items: center;
          justify-content: center;

          border-radius: 8px;

          background: #eef0ff;
          color: #696cff;

          font-size: 17px;
        }

        .summary-top small {
          color: #989da7;

          font-size: 10px;
          font-weight: 700;

          text-transform: uppercase;
        }

        .monthly-summary-card.present
        .summary-top > span {
          background: #e9f8ef;
          color: #26985a;
        }

        .monthly-summary-card.absent
        .summary-top > span {
          background: #ffeded;
          color: #d84546;
        }

        .monthly-summary-card.pending
        .summary-top > span {
          background: #fff3e2;
          color: #d0841e;
        }

        .monthly-summary-card.low
        .summary-top > span {
          background: #fff0f0;
          color: #d84546;
        }

        .monthly-summary-card.average
        .summary-top > span {
          background: #eaf6ff;
          color: #3289c8;
        }

        .monthly-summary-card > strong {
          display: block;

          margin-top: 10px;

          color: #393e47;

          font-size: 25px;

          line-height: 1;
        }

        .monthly-summary-card p {
          margin: 7px 0 0;

          color: #979ca7;

          font-size: 10.5px;

          line-height: 1.4;
        }

        /* LEGEND */

        .monthly-legend {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 7px;

          padding: 9px 12px;
          margin-bottom: 14px;

          border: 1px solid #e2e5eb;
          border-radius: 10px;

          background: #fff;
        }

        .monthly-legend > strong {
          margin-right: 4px;

          color: #555a64;

          font-size: 11px;
        }

        .monthly-legend > span {
          display: inline-flex;
          align-items: center;
          gap: 4px;

          padding: 4px 7px;

          border-radius: 5px;

          font-size: 10px;
          font-weight: 800;
        }

        .monthly-legend span small {
          font-size: 9px;
          font-weight: 600;
        }

        .monthly-legend .present {
          background: #e9f8ef;
          color: #268e56;
        }

        .monthly-legend .absent {
          background: #ffeded;
          color: #cf4041;
        }

        .monthly-legend .not-marked {
          background: #fff3e2;
          color: #c87c18;
        }

        .monthly-legend .holiday,
        .monthly-legend .na {
          background: #f0f1f4;
          color: #717681;
        }

        .monthly-legend .leave {
          background: #f4efff;
          color: #7955c7;
        }

        /* REGISTER */

        .monthly-register-card {
          overflow: hidden;

          border: 1px solid #e1e4ea;
          border-radius: 14px;

          background: #fff;
        }

        .monthly-table-heading {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 15px;

          padding: 15px;

          border-bottom:
            1px solid #e9ebef;
        }

        .monthly-table-heading h2 {
          margin: 0;

          color: #414650;

          font-size: 16px;
          font-weight: 700;
        }

        .monthly-table-heading p {
          margin: 4px 0 0;

          color: #969ba6;

          font-size: 11px;
        }

        .monthly-table-tools {
          display: flex;
          align-items: center;
          gap: 7px;
        }

        .monthly-view-select,
        .monthly-search {
          position: relative;
        }

        .monthly-view-select > i,
        .monthly-search > i {
          position: absolute;

          top: 50%;
          left: 9px;

          transform:
            translateY(-50%);

          color: #9499a4;

          font-size: 14px;

          pointer-events: none;
        }

        .monthly-view-select select {
          height: 39px;
          min-width: 145px;

          padding:
            7px 9px 7px 29px;

          border: 1px solid #dfe2e9;
          border-radius: 8px;

          background: #fff;
          color: #555a64;

          font-size: 11px;
        }

        .monthly-search input {
          width: 255px;
          height: 39px;

          padding:
            7px 31px 7px 29px;

          border: 1px solid #dfe2e9;
          border-radius: 8px;

          outline: none;

          color: #50555f;

          font-size: 11px;
        }

        .monthly-search button {
          position: absolute;

          top: 50%;
          right: 5px;

          transform:
            translateY(-50%);

          border: 0;

          background: transparent;
          color: #999;

          cursor: pointer;
        }

        .monthly-small-excel {
          height: 39px;

          display: inline-flex;
          align-items: center;
          gap: 5px;

          padding: 7px 11px;

          border: 1px solid #d7e9de;
          border-radius: 8px;

          background: #effaf3;
          color: #278653;

          font-size: 11px;
          font-weight: 700;

          cursor: pointer;
        }

        /* TABLE */

        .monthly-table-scroll {
          position: relative;

          width: 100%;

          overflow: auto;

          max-height: 68vh;
        }

        .monthly-table {
          width: max-content;
          min-width: 100%;

          border-collapse: separate;
          border-spacing: 0;
        }

        .monthly-table th {
          position: sticky;

          top: 0;

          z-index: 20;

          height: 48px;

          padding: 7px 6px;

          border-right:
            1px solid #e0e3e9;

          border-bottom:
            1px solid #dfe2e8;

          background: #f6f7f9;
          color: #656a75;

          font-size: 10.5px;
          font-weight: 700;

          text-align: center;

          white-space: nowrap;
        }

        .monthly-table td {
          height: 51px;

          padding: 6px;

          border-right:
            1px solid #eceef2;

          border-bottom:
            1px solid #eceef2;

          background: #fff;
          color: #555a64;

          font-size: 11px;

          text-align: center;
          vertical-align: middle;
        }

        .monthly-table tbody
        tr:hover td {
          background: #fafbfc;
        }

        .monthly-table
        .sticky-col {
          position: sticky;

          z-index: 12;

          background: #fff;
        }

        .monthly-table
        th.sticky-col {
          z-index: 30;

          background: #f6f7f9;
        }

        .serial-col {
          left: 0;

          width: 42px;
          min-width: 42px;
          max-width: 42px;
        }

        .student-col {
          left: 42px;

          width: 180px;
          min-width: 180px;
          max-width: 180px;

          text-align: left !important;
        }

        .reg-col {
          left: 222px;

          width: 105px;
          min-width: 105px;
          max-width: 105px;

          box-shadow:
            5px 0 7px
            rgba(30,35,55,.035);
        }

        .day-col,
        .monthly-day-cell {
          width: 48px;
          min-width: 48px;
          max-width: 48px;
        }

        .day-col strong {
          display: block;
          font-size: 11px;
        }

        .day-col small {
          display: block;

          margin-top: 1px;

          color: #9a9faa;

          font-size: 8px;
        }

        .holiday-day {
          background:
            #f0f1f4 !important;
        }

        .holiday-cell {
          background:
            #fafafa !important;
        }

        .overview-col {
          width: 145px;
          min-width: 145px;
          max-width: 145px;
        }

        .percentage-col {
          width: 90px;
          min-width: 90px;
          max-width: 90px;
        }

        .monthly-student {
          display: flex;
          align-items: center;
          gap: 8px;

          text-align: left;
        }

        .monthly-avatar {
          width: 33px;
          height: 33px;

          display: flex;
          align-items: center;
          justify-content: center;

          flex: 0 0 33px;

          border-radius: 50%;

          background: #eef0ff;
          color: #696cff;

          font-size: 9px;
          font-weight: 800;
        }

        .monthly-student
        > div:last-child {
          min-width: 0;

          display: flex;
          flex-direction: column;
        }

        .monthly-student strong {
          overflow: hidden;

          color: #424751;

          font-size: 11.5px;

          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .monthly-student span {
          overflow: hidden;

          margin-top: 2px;

          color: #999ea9;

          font-size: 9px;

          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .monthly-reg {
          display: block;

          color: #4d525c;

          font-size: 10px;
        }

        .reg-col small {
          display: block;

          margin-top: 2px;

          color: #9a9faa;

          font-size: 8px;
        }

        .monthly-code {
          display: inline-flex;
          align-items: center;
          justify-content: center;

          min-width: 26px;
          min-height: 25px;

          padding: 3px;

          border-radius: 5px;

          font-size: 10px;
          font-weight: 800;
        }

        .monthly-code small {
          margin-left: 1px;
          font-size: 7px;
        }

        .monthly-code.present {
          background: #e8f8ef;
          color: #248c53;
        }

        .monthly-code.absent {
          background: #ffebeb;
          color: #d13d3e;
        }

        .monthly-code.not-marked {
          background: #fff2df;
          color: #c47918;
        }

        .monthly-code.holiday {
          background: #eef0f3;
          color: #737883;
        }

        .monthly-code.na {
          background: #f1f2f4;
          color: #8b8f98;

          font-size: 8px;
        }

        .monthly-code.leave {
          background: #f2edff;
          color: #7754c3;
        }

        .monthly-future {
          color: #c0c3ca;
          font-size: 10px;
        }

        .monthly-time-cell {
          display: flex;
          align-items: center;
          justify-content: center;
          flex-direction: column;

          gap: 1px;

          line-height: 1.1;
        }

        .monthly-in-time {
          color: #278b56;

          font-size: 7.5px;
          font-weight: 800;
        }

        .monthly-out-time {
          color: #696cff;

          font-size: 7.5px;
          font-weight: 700;
        }

        .monthly-time-cell small {
          color: #8b63d0;
          font-size: 7px;
        }

        .monthly-overview {
          display: flex;
          align-items: center;
          justify-content: center;

          gap: 5px;
        }

        .monthly-overview span {
          display: inline-flex;
          align-items: center;

          gap: 2px;

          font-size: 8px;
          font-weight: 700;
        }

        .monthly-overview .p {
          color: #278e55;
        }

        .monthly-overview .a {
          color: #cf4142;
        }

        .monthly-overview .n {
          color: #c17a1c;
        }

        .monthly-overview b {
          font-size: 10px;
        }

        .working-days {
          display: block;

          margin-top: 4px;

          color: #999da7;

          font-size: 8px;
        }

        .monthly-percentage {
          display: inline-flex;
          align-items: center;
          justify-content: center;

          min-width: 55px;

          padding: 5px 6px;

          border-radius: 6px;

          font-size: 10px;
          font-weight: 800;
        }

        .monthly-percentage.excellent {
          background: #e8f8ef;
          color: #258c53;
        }

        .monthly-percentage.good {
          background: #eef0ff;
          color: #696cff;
        }

        .monthly-percentage.low {
          background: #ffebeb;
          color: #cf3d3e;
        }

        .low-label {
          display: block;

          margin-top: 3px;

          color: #d34849;

          font-size: 7px;
          font-weight: 700;
        }

        .low-attendance-row
        .student-col {
          border-left:
            3px solid #e35b5c;
        }

        .monthly-empty {
          height: 220px !important;

          text-align:
            center !important;
        }

        .monthly-empty i {
          display: block;

          margin-bottom: 7px;

          color: #a2a6af;

          font-size: 30px;
        }

        .monthly-empty strong {
          display: block;

          color: #555a64;

          font-size: 13px;
        }

        .monthly-empty span {
          display: block;

          margin-top: 4px;

          color: #999da7;

          font-size: 10px;
        }

        .monthly-table-footer {
          display: flex;
          align-items: center;
          justify-content: space-between;

          gap: 10px;

          padding: 9px 13px;

          border-top:
            1px solid #e7e9ee;

          background: #fafbfc;
          color: #8d929d;

          font-size: 9.5px;
        }

        .monthly-table-footer span {
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .monthly-table-footer strong {
          color: #656a75;
        }

        .monthly-spin {
          display: inline-block;

          animation:
            monthlySpin 1s
            linear infinite;
        }

        @keyframes monthlySpin {
          to {
            transform: rotate(360deg);
          }
        }

        /* RESPONSIVE */

        @media
        (max-width: 1450px) {

          .monthly-filter-grid {
            grid-template-columns:
              repeat(3, 1fr);
          }

          .monthly-summary-grid {
            grid-template-columns:
              repeat(3, 1fr);
          }

        }

        @media
        (max-width: 1050px) {

          .monthly-header {
            align-items: flex-start;
            flex-direction: column;
          }

          .monthly-header-actions {
            width: 100%;
          }

          .monthly-report-bar {
            align-items: flex-start;
            flex-direction: column;
          }

          .monthly-report-tags {
            justify-content: flex-start;
          }

          .monthly-table-heading {
            align-items: flex-start;
            flex-direction: column;
          }

          .monthly-table-tools {
            width: 100%;
          }

          .monthly-search {
            flex: 1;
          }

          .monthly-search input {
            width: 100%;
          }

        }

        @media
        (max-width: 767px) {

          .monthly-main {
            padding:
              85px 10px 30px;
          }

          .monthly-header {
            padding: 14px;
          }

          .monthly-header-icon {
            display: none;
          }

          .monthly-header h1 {
            font-size: 20px;
          }

          .monthly-header p {
            font-size: 12px;
          }

          .monthly-header-actions {
            display: grid;

            grid-template-columns:
              repeat(3, 1fr);

            gap: 6px;
          }

          .monthly-action {
            width: 100%;

            padding: 8px 7px;

            font-size: 11px;
          }

          .monthly-print-wrapper {
            width: 100%;
          }

          .monthly-filter-card {
            padding: 14px;
          }

          .monthly-filter-grid {
            grid-template-columns: 1fr;
          }

          .monthly-field label {
            font-size: 13px;
          }

          .monthly-control select,
          .monthly-control input {
            height: 46px;

            font-size: 14px;
          }

          .batch-selection-empty {
            min-height: 330px;

            padding:
              35px 15px;
          }

          .batch-selection-empty h2 {
            font-size: 18px;
          }

          .batch-selection-empty > p {
            font-size: 12px;
          }

          .monthly-summary-grid {
            grid-template-columns:
              repeat(2, 1fr);
          }

          .monthly-summary-card {
            min-height: 125px;
          }

          .monthly-summary-card
          > strong {
            font-size: 23px;
          }

          .monthly-legend {
            overflow-x: auto;
            flex-wrap: nowrap;
          }

          .monthly-legend > * {
            flex-shrink: 0;
          }

          .monthly-table-tools {
            align-items: stretch;
            flex-direction: column;
          }

          .monthly-view-select select,
          .monthly-search input,
          .monthly-small-excel {
            width: 100%;
            min-width: 0;
          }

          .student-col {
            width: 155px;
            min-width: 155px;
            max-width: 155px;
          }

          .reg-col {
            left: 197px;

            width: 92px;
            min-width: 92px;
            max-width: 92px;
          }

          .monthly-table-scroll {
            max-height: 70vh;
          }

        }

        @media
        (max-width: 430px) {

          .monthly-header-actions {
            grid-template-columns:
              1fr 1fr;
          }

          .monthly-summary-grid {
            gap: 7px;
          }

          .monthly-summary-card {
            padding: 11px;
          }

          .summary-top small {
            font-size: 8px;
          }

          .monthly-summary-card p {
            font-size: 9px;
          }

        }

      
        /* APPLICATION SHELL RESPONSIVE */
        @media (max-width: 1199.98px) {
          .monthly-container { padding: 14px; }
          .monthly-master-v2-wrapper { margin-bottom: 14px; }
        }

        @media (max-width: 991.98px) {
          .monthly-container { padding: 12px; }
          .monthly-master-v2-wrapper { margin-bottom: 12px; }
          .monthly-main {
            width: 100%;
            max-width: 100%;
            min-width: 0;
            margin: 0;
            padding: 0;
          }
        }

        @media (max-width: 575.98px) {
          .monthly-container { padding: 10px; }
          .monthly-master-v2-wrapper { margin-bottom: 10px; }
          .monthly-main { padding: 0; }
        }

        @media print {
          .monthly-attendance-page .no-print,
          .monthly-master-v2-wrapper {
            display: none !important;
          }
        }


        /* SCAI ATTENDANCE: flush navbar fix, isolated to this page.
           The global template otherwise adds padding above .layout-page. */
        html.layout-navbar-fixed .layout-wrapper.monthly-attendance-page:not(.layout-without-menu) > .layout-container > .layout-page,
        html.layout-navbar-fixed .layout-wrapper.monthly-attendance-page .layout-page,
        .layout-wrapper.monthly-attendance-page > .layout-container > .layout-page {
          padding-top: 0 !important;
          margin-top: 0 !important;
        }
        .layout-wrapper.monthly-attendance-page > .layout-container {
          padding-top: 0 !important;
          margin-top: 0 !important;
        }
        .layout-wrapper.monthly-attendance-page .layout-page > .no-print:first-child {
          padding-top: 0 !important;
          margin-top: 0 !important;
        }
        .layout-wrapper.monthly-attendance-page #layout-navbar.scai-topbar {
          top: 0 !important;
          margin: 0 !important;
          width: 100% !important;
          max-width: none !important;
          border-radius: 0 !important;
        }
`}</style>
    </>
  );
}